import type { QueryClient } from '@tanstack/react-query';
import {
  SearchError,
  searchProducts,
  type ProductSearchParams,
  type ProductSearchResponse,
} from '@/features/self-service/api/search';
import { storefrontApi } from './client';
import { storefrontKeys } from './keys';
import type { StorefrontSearchReason } from './types';

/** Opções da chave de busca. Sem observer: só `ensureQueryData` a lê. */
export function searchAccessOptions() {
  return {
    queryKey: storefrontKeys.searchAccess(),
    queryFn: () => storefrontApi.searchAccess(),
    // Abaixo do TTL de 12h da chave com folga larga. O que a renova de fato é
    // a invalidação disparada pelo 401, não o relógio.
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
  };
}

/**
 * Não há chave para este buyer, e o motivo importa.
 *
 * `no_brands` é cadastro (um ADMIN resolve em segundos); `search_unavailable` é
 * infraestrutura. Colapsados, o suporte investiga o segundo quando o caso é o
 * primeiro.
 */
export class StorefrontSearchUnavailable extends Error {
  reason: StorefrontSearchReason;
  constructor(reason: StorefrontSearchReason) {
    super(
      reason === 'no_brands'
        ? 'Nenhuma marca liberada para esta conta'
        : 'Busca indisponível'
    );
    this.name = 'StorefrontSearchUnavailable';
    this.reason = reason;
  }
}

/**
 * Uma busca contra o Typesense com a chave escopada da vitrine.
 *
 * A chave é lida por `ensureQueryData` em vez de vir por parâmetro, e é isso
 * que torna a recuperação do 401 possível sem reload: **401 aqui é chave
 * rotacionada, não erro de rede** — editar o `allowed_brands` de qualquer buyer
 * derruba a chave-pai da vitrine, e a rotação é evento normal desde o ticket 02.
 * Invalidada a query de acesso, o retry seguinte reemite e refaz a busca com o
 * recorte já novo.
 */
export async function searchWithScopedKey(
  queryClient: QueryClient,
  params: ProductSearchParams
): Promise<ProductSearchResponse> {
  const access = await queryClient.ensureQueryData(searchAccessOptions());
  if (!access.search) {
    throw new StorefrontSearchUnavailable(access.reason ?? 'search_unavailable');
  }

  try {
    return await searchProducts(access.search, params);
  } catch (error) {
    if (error instanceof SearchError && error.status === 401) {
      await queryClient.invalidateQueries({
        queryKey: storefrontKeys.searchAccess(),
      });
    }
    throw error;
  }
}

/**
 * Política de retry compartilhada pela listagem.
 *
 * O 401 é retentado **uma vez e sem espera**: a chave já foi invalidada acima,
 * então a tentativa seguinte busca uma nova. Atrasar transformaria uma rotação
 * invisível num piscar de "busca indisponível".
 */
export function searchRetry(count: number, error: Error): boolean {
  if (error instanceof StorefrontSearchUnavailable) return false;
  return count < 1;
}

export function searchRetryDelay(_count: number, error: Error): number {
  return error instanceof SearchError && error.status === 401 ? 0 : 800;
}
